/* eslint-disable @typescript-eslint/ban-types */
import * as d3 from 'd3';

import { textSize, TextSize, wrapText } from './texts';
import { hex2rgba } from './colors/hex2rgba';

export interface IHeatmap {
  axisXLabels: Array<{ name: string; description: string }>;
  axisYLabels: Array<{ name: string; id: string; description: string }>;
  values: { XLabel: string; YLabel: string; YLabelID: string; Value: number }[];
  matrix: number[][];
}

const replaceLabel = (label: string) =>
  label
    ?.replace(/ /g, 'SPACEINCLASSNAME')
    .replace(/&/g, 'ANDINCLASSNAME')
    .replace(/\//g, 'SLASHINCLASSNAME')
    .replace(/:/g, 'DOTSINCLASSNAME')
    .replace(/\./g, 'DOTINCLASSNAME');

const margin = { top: 5, right: 12, bottom: 5, left: 5 };

const getColors = (min: number, max: number, colorScheme: string[]) =>
  d3
    .scaleLinear()
    .range(colorScheme as any)
    .domain([min, -1, -0.1, 0, 0.1, 1, max]);

export class HeatmapHelper {
  private _heatmap!: IHeatmap;
  private _heatmapWidth!: number;
  private _heatmapHeight!: number;
  private _colorScheme!: string[];
  private _tooltipCfg!: any;
  private _gridIndex!: string;
  private _onClick!: Function;
  private namesOnTop = true;

  private _svg!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private _g!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private _axisX!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private _axisY!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private _sortLayer!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private _tooltip!: d3.Selection<HTMLDivElement, unknown, null, undefined>;
  private _prevAxisX!: d3.ScaleBand<any>;
  private _prevAxisY!: d3.ScaleBand<any>;
  private _onSort: Function;
  private _sorting: { col: string; row: string; rowSortAscending: boolean; colSortAscending: boolean };

  constructor(
    private site: HTMLElement,
    onSort: Function,
  ) {
    this._sorting = { col: 'alpha', row: 'alpha', rowSortAscending: true, colSortAscending: true };
    this._onSort = onSort;
    this._svg = d3.select(this.site).append('svg') as any;
    this._g = this._svg.append('g').attr('class', 'base').attr('transform', `translate(${margin.left}, ${margin.top})`);
    this._sortLayer = this._g.append('g').attr('class', 'sort-layer');
    this._axisX = this._g.append('g').attr('class', 'axis-x');
    this._axisY = this._g.append('g').attr('class', 'axis-y');

    const sortLayerX = this._sortLayer.append('g').attr('class', 'sort-layer-x');
    const sortLayerY = this._sortLayer.append('g').attr('class', 'sort-layer-y');
    sortLayerX.on('mouseover', () => sortLayerX.selectAll('.x-sort').style('visibility', 'visible'));
    sortLayerX.on('mouseout', () => {
      sortLayerX.selectAll('.x-sort').style('visibility', 'hidden');
      sortLayerX
        .select(`[column-name='${this._sorting.row}']`)
        .select(this._sorting.rowSortAscending ? '.sort-asc' : '.sort-desc')
        .style('visibility', 'visible');
    });
    sortLayerY.on('mouseover', () => {
      sortLayerY.selectAll('.y-sort').style('visibility', 'visible');
      sortLayerY
        .select(`[row-name='${this._sorting.col}']`)
        .select(this._sorting.colSortAscending ? '.sort-asc' : '.sort-desc')
        .style('visibility', 'visible');
    });
    sortLayerY.on('mouseout', () => sortLayerY.selectAll('.y-sort').style('visibility', 'hidden'));
    sortLayerX.append('rect').attr('class', 'sort-background-x').style('fill', '#fff').style('opacity', 0.01);
    sortLayerY.append('rect').attr('class', 'sort-background-y').style('fill', '#fff').style('opacity', 0.01);

    this._tooltip = d3
      .select(this.site)
      .append('div')
      .style('opacity', 0)
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('z-index', '99');
    this._g.on('mouseout', () => {
      this._tooltip.style('left', '-1200px').style('top', '-1200px').style('opacity', 0);
    });
  }

  public setSorting(sorting: { col: string; row: string; rowSortAscending: boolean; colSortAscending: boolean }) {
    this._sorting = sorting;
  }

  public setOnClick(onClick: Function) {
    this._onClick = onClick;
  }

  public setSize(width: number, height: number): void {
    this.updateContainerSizes(width, height);
    this.updateHeatmapSize(width, height);
    this.updateData();
  }

  public setData(data: IHeatmap, colorScheme: string[], tooltipCfg: any, gridIndex: string) {
    const orderIndex = data.axisXLabels.findIndex((l: any) => l.name === 'order');

    const cleanMatrix = data.matrix.map((m: any) => m.splice(orderIndex, 1));

    this._heatmap = {
      ...data,
      axisXLabels: data.axisXLabels.filter((l: any) => l.name !== 'order'),
      matrix: cleanMatrix,
    };
    this._colorScheme = colorScheme;
    this._tooltipCfg = tooltipCfg;
    this._gridIndex = gridIndex;
    this.updateData();
  }

  private namesOnTopChanged(): void {
    this.updateHeatmapSize(this._heatmapWidth, this._heatmapHeight);
  }

  private changeNamesAlign(): void {
    this.namesOnTop = !this.namesOnTop;
    this.namesOnTopChanged();
  }

  private updateCommon(): {
    names: d3.Selection<d3.BaseType, { name: string; description: string }, d3.BaseType, unknown>;
    rows: d3.Selection<d3.BaseType, { name: string; description: string; id: string }, d3.BaseType, unknown>;
    bars: d3.Selection<
      d3.BaseType,
      { XLabel: string; YLabel: string; YLabelID: string; Value: number },
      d3.BaseType,
      unknown
    >;
    x: d3.ScaleBand<string>;
    y: d3.ScaleBand<string>;
    myColor: d3.ScaleLinear<number, number>;
    sin: number;
    cos: number;
    nameX(d: string): number;
    nameY(d: string): number;
    nameTransform(d: string): string;
    rowX(d: string): number;
    rowY(d: string): number;
  } {
    const base = this._svg.select('.base');
    const axisX = this._svg.select('.axis-x');
    const axisY = this._svg.select('.axis-y');
    const bars = base.selectAll('.bar').data(this._heatmap.values.filter((v: any) => v.XLabel !== 'order'));
    const names = axisX.selectAll('.name').data(this._heatmap.axisXLabels);
    const rows = axisY.selectAll('.row').data(this._heatmap.axisYLabels);

    let maxWidthOfLabel = 0;
    this._heatmap.axisYLabels.forEach(row => {
      const size = textSize(row.name.substring(0, 20), true);
      maxWidthOfLabel = Math.max(maxWidthOfLabel, size.width + 10);
    });

    const hw = this._heatmapWidth - this._heatmap.axisXLabels.length * 0.5;

    const x: d3.ScaleBand<string> = d3
      .scaleBand()
      .domain(this._heatmap.axisXLabels.map(({ name }) => name))
      .range([maxWidthOfLabel + 34, hw])
      .padding(0.01) as d3.ScaleBand<string>;

    let maxNameWidth = 0;
    let maxNameHeight = 0;
    let maxLineCount = 0;
    const nameSizes: { [id: string]: TextSize } = {};
    this._heatmap.axisXLabels.forEach(({ name }) => {
      const size = textSize(name);
      nameSizes[name] = size;
      maxNameWidth = Math.max(maxNameWidth, size.width);
      maxNameHeight = Math.max(maxNameHeight, size.height);
      maxLineCount = Math.max(maxLineCount, size.lineCount);
    });

    let alpha = 0;
    let projectionNameLength = maxNameWidth;
    while (projectionNameLength > x.bandwidth() && alpha > -90) {
      alpha -= 1;
      projectionNameLength =
        maxNameWidth * Math.cos((-alpha * Math.PI) / 180) + maxNameHeight * Math.sin((-alpha * Math.PI) / 180);
    }

    const sin = Math.sin((-alpha * Math.PI) / 180);
    const cos = Math.cos((-alpha * Math.PI) / 180);

    const mw1 = maxNameWidth * cos + maxNameHeight * sin;
    const mh1 = maxNameHeight * cos + maxNameWidth * sin;
    maxNameWidth = mw1;
    maxNameHeight = mh1;

    this._heatmap.axisXLabels.forEach(({ name }) => {
      const size = nameSizes[name];
      const width = size.width * cos + size.height * sin;
      const height = size.height * cos + size.width * sin;
      size.width = width;
      size.height = height;
    });

    const maxHeightOfLabel = maxNameHeight;

    let axisRange: [number, number];
    if (this.namesOnTop) {
      axisRange = [maxHeightOfLabel, this._heatmapHeight];
    } else {
      axisRange = [0, this._heatmapHeight - maxHeightOfLabel];
    }

    const y: d3.ScaleBand<string> = d3
      .scaleBand()
      .domain(this._heatmap.axisYLabels.map(({ name }) => name))
      .range(axisRange)
      .padding(0.01) as d3.ScaleBand<string>;

    const min: number = d3.min(this._heatmap.values, v => v.Value) as number;
    const max: number = d3.max(this._heatmap.values, v => v.Value) as number;
    const myColor: any = getColors(min, max, this._colorScheme);

    const nameX = (d: string) => {
      if (!this.namesOnTop && alpha !== 0) {
        return (x(d) as number) + x.bandwidth() / 2;
      }

      let result = (x(d) as number) + x.bandwidth() / 2;
      if (!this.namesOnTop || alpha !== 0) {
        result -= nameSizes[d].width / 2;
      }
      return result;
    };

    const nameY = (d: string) => {
      if (this.namesOnTop) {
        if (alpha === 0) {
          return maxNameHeight / maxLineCount + maxNameHeight - nameSizes[d].height - 4;
        }

        return maxNameHeight - 4;
      }

      if (alpha === 0) {
        return this._heatmapHeight - maxNameHeight + maxNameHeight / maxLineCount - 2;
      }

      return this._heatmapHeight - maxNameHeight;
    };

    const nameTransform = (_d: string) => {
      return `rotate(${alpha})`;
    };

    const rowX = (_d: string) => {
      const result = 32;
      return result;
    };

    const rowY = (d: string) => {
      const result = (y(d) as number) + y.bandwidth() / 2 + 4;
      return result;
    };

    return {
      names,
      rows,
      bars,
      x,
      y,
      myColor,
      sin,
      cos,
      nameX,
      nameY,
      nameTransform,
      rowX,
      rowY,
    };
  }

  private updateHeatmapSize(width: number, height: number): void {
    this._heatmapWidth = width;
    this._heatmapHeight = height;

    if (!this._heatmap) {
      return;
    }

    const r = this.updateCommon();

    r.names
      .transition()
      .duration(150)
      .attr('transform', d => `translate(${r.nameX(d.name)}, ${r.nameY(d.name)}) ${r.nameTransform(d.name)}`)
      .call(wrapText as any, this.namesOnTop, r.sin, r.cos);

    r.rows
      .transition()
      .duration(150)
      .attr('x', d => r.rowX(d.name) - 10)
      .attr('y', d => r.rowY(d.name));

    r.bars
      .transition()
      .duration(150)
      .attr('width', () => r.x.bandwidth())
      .attr('height', () => r.y.bandwidth())
      .style('fill', d => r.myColor(d.Value))
      .attr('x', d => r.x(d.XLabel) as any)
      .attr('y', d => r.y(d.YLabel) as any);
  }

  private renderTooltip(elemRect: DOMRect, header: string, content: string | undefined) {
    if (!content) return;

    const siteRect = d3.select(this.site).node()!.getBoundingClientRect();
    const {
      letterSpacing,
      headerFontWeight,
      headerFontSize,
      headerMarginBottom,
      contentFontWeight,
      contentFontSize,
      color,
      backgroundColor,
      opacity,
      borderRadius,
      padding,
      maxWidth,
      borderWidth,
      borderStyle,
      borderColor,
    } = this._tooltipCfg;
    const headerLineHeight = Number(String(headerFontSize).replace('px', '')) * 1.25;
    const contentLineHeight = Number(String(contentFontSize).replace('px', '')) * 1.25;
    this._tooltip.html(
      content !== 'undefined'
        ? `<div class="tooltip-wrapper">
          <div style='letter-spacing:${letterSpacing}px;font-weight:${headerFontWeight};font-size:${headerFontSize};line-height:${headerLineHeight}px;margin-bottom:${
            Number(headerMarginBottom.replace('px', '')) - 5
          }px;'>${header}</div>
          <div style='letter-spacing:${letterSpacing}px;font-weight:${contentFontWeight};font-size:${contentFontSize};line-height:${contentLineHeight}px;margin:0px;'>${content}</div>
        </div>
        <div class="tooltip-arrow"/><div class="tooltip-arrow-blocker"/>`
        : `<div class="tooltip-wrapper">
          <div style='letter-spacing:${letterSpacing}px;font-weight:${headerFontWeight};font-size:${headerFontSize};line-height:${headerLineHeight}px;margin-bottom:${
            Number(headerMarginBottom.replace('px', '')) - 5
          }px;'>${header}</div>
        </div>
        <div class="tooltip-arrow"/><div class="tooltip-arrow-blocker"/>`,
    );
    const tooltipRect = this._tooltip.node()!.getBoundingClientRect();
    let fluidPosition = 'top';

    const tooltipSettings = {
      offsetTop: 6,
      offsetLeft: 6,
    };

    const tooltipPosition = {
      left: elemRect.left - siteRect.left + elemRect.width / 2 - tooltipRect.width / 2,
      top: elemRect.top - siteRect.top,
      height: tooltipRect.height,
      width: tooltipRect.width,
    };

    if (tooltipPosition.left < elemRect.width + tooltipSettings.offsetLeft) {
      fluidPosition = 'right';
      tooltipPosition.top += elemRect.height / 2 - tooltipPosition.height / 2;
      tooltipPosition.left += elemRect.width / 2 + tooltipSettings.offsetLeft + tooltipPosition.width / 2 + 5;
    }

    if (tooltipPosition.left + tooltipRect.width > siteRect.width) {
      fluidPosition = 'left';
      tooltipPosition.top += elemRect.height / 2 - tooltipPosition.height / 2;
      tooltipPosition.left -= elemRect.width / 2 + tooltipSettings.offsetLeft + tooltipPosition.width / 2;
    }

    if (
      tooltipPosition.top < tooltipPosition.height + tooltipSettings.offsetTop * 4 &&
      fluidPosition !== 'right' &&
      fluidPosition !== 'left'
    ) {
      tooltipPosition.top += tooltipSettings.offsetTop + elemRect.height;
      fluidPosition = 'bottom';
    }

    if (fluidPosition === 'top') {
      tooltipPosition.top += -tooltipRect.height - tooltipSettings.offsetTop;
    }

    const tooltipBorder = `${borderWidth} ${borderStyle} ${hex2rgba(borderColor, opacity)}`;
    this._tooltip
      .attr('class', `tooltip tooltip-position--${fluidPosition}`)
      .style('left', `${tooltipPosition.left}px`)
      .style('top', `${tooltipPosition.top}px`)
      .style('color', color)
      .style('background-color', hex2rgba(backgroundColor, opacity))
      .style('border-top', tooltipBorder)
      .style('border-right', tooltipBorder)
      .style('border-bottom', tooltipBorder)
      .style('border-left', tooltipBorder)
      .style('border-radius', borderRadius)
      .style('letter-spacing', letterSpacing)
      .style('padding', padding)
      .style('max-width', maxWidth);

    this._tooltip.transition().style('opacity', 1);
  }

  private setEventHandlersForBars<T extends d3.BaseType>(
    sel: d3.Selection<T, { XLabel: string; YLabel: string; YLabelID: string; Value: number }, d3.BaseType, unknown>,
  ): d3.Selection<T, { XLabel: string; YLabel: string; YLabelID: string; Value: number }, d3.BaseType, unknown> {
    return sel
      .on('click', (d: any, n: any) => {
        this._onClick({
          source: 'value',
          topic: n.YLabelID,
          symbol: n.XLabel,
        });
      })
      .on('mouseover', (d: any, n: any) => {
        const row = this._heatmap.axisYLabels.findIndex(label => label.name === d.YLabel);
        const col = this._heatmap.axisXLabels.findIndex(label => label.name === d.XLabel);

        d3.select(`.row-index-${row}-${this._gridIndex}`).attr('font-weight', 'bold');
        d3.select(`.name-index-${col}-${this._gridIndex}`).attr('font-weight', 'bold');
        d3.select(`.index-${d.srcElement.className.baseVal.split('-')[1]}-${this._gridIndex}`).style('opacity', '1');

        const barRect = (
          d3.select(`.index-${d.srcElement.className.baseVal.split('-')[1]}-${this._gridIndex}`).node() as HTMLElement
        ).getBoundingClientRect();

        this.renderTooltip(
          barRect,
          `<span>${n.XLabel} and ${n.YLabel}</span>`,
          `<span>value: ${n?.Value?.toFixed(3) || 0}</span>`,
        );
      })
      .on('mouseout', (d: any) => {
        const row = this._heatmap.axisYLabels.findIndex(label => label.name === d.YLabel);
        const col = this._heatmap.axisXLabels.findIndex(label => label.name === d.XLabel);

        d3.select(`.index-${d.srcElement.className.baseVal.split('-')[1]}-${this._gridIndex}`).style('opacity', '0.8');
        d3.select(`.row-index-${row}-${this._gridIndex}`).attr('font-weight', 'normal');
        d3.select(`.name-index-${col}-${this._gridIndex}`).attr('font-weight', 'normal');
        this._tooltip.style('opacity', 0);
      });
  }

  private addSortControl(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    label: string,
    isColumn: boolean,
  ): void {
    const isActiveSort = (isColumn && this._sorting.row === label) || (!isColumn && this._sorting.col === label);
    const sortAscending = isColumn ? this._sorting.rowSortAscending : this._sorting.colSortAscending;

    function onHoverIn(this: any) {
      const control = d3.select(this);
      control.style('fill', '#000').style('stroke', '#000');
    }
    function onHoverOut(this: any) {
      const control = d3.select(this);
      const isAscending = control.classed('sort-asc');
      if (isActiveSort && isAscending === sortAscending) return;
      control.style('fill', '#ccc').style('stroke', '#ccc');
    }

    g.append('path')
      .classed('sort-asc', true)
      .attr('d', 'M 0,3 L 4,-2 L 8,3 Z')
      .style('fill', isActiveSort && sortAscending ? '#000' : '#ccc')
      .style('stroke', isActiveSort && sortAscending ? '#000' : '#ccc')
      .on('click', () => this._onSort(label, isColumn, true));
    g.append('path')
      .classed('sort-desc', true)
      .attr('d', 'M 0,6 L 4,11 L 8,6 Z')
      .style('fill', isActiveSort && !sortAscending ? '#000' : '#ccc')
      .style('stroke', isActiveSort && !sortAscending ? '#000' : '#ccc')
      .on('click', () => this._onSort(label, isColumn, false));
    g.selectAll('path').style('cursor', 'pointer').on('mouseenter', onHoverIn).on('mouseleave', onHoverOut);
  }

  private updateSortlayer(r: { x: d3.ScaleBand<string>; y: d3.ScaleBand<string>; nameY(d: string): number }): void {
    const firstXPos = r.x(this._heatmap.axisXLabels[0]?.name) as number;
    const firstYPos = r.y(this._heatmap.axisYLabels[0]?.name) as number;
    this._sortLayer.selectAll('.x-sort,.y-sort').remove();
    const sortLayerX = this._sortLayer.select('.sort-layer-x');
    const sortLayerY = this._sortLayer.select('.sort-layer-y');
    sortLayerX
      .select('rect')
      .attr('x', firstXPos)
      .attr('y', 0)
      .attr('width', (r.x.bandwidth() + 0.5) * this._heatmap?.axisXLabels?.length || 0)
      .attr('height', firstYPos);
    sortLayerY
      .select('rect')
      .attr('x', 0)
      .attr('y', firstYPos)
      .attr('width', firstXPos)
      .attr('height', (r.y.bandwidth() + 0.5) * this._heatmap?.axisYLabels?.length || 0);

    const posX = (index: number) => firstXPos - 8 + (index + 1) * (r.x.bandwidth() + 0.25);
    const posY = (index: number) => firstYPos - 4 + (index + 0.5) * (r.y.bandwidth() + 0.5);
    this._heatmap.axisXLabels.forEach((label, index) => {
      const g = sortLayerX.append('g').classed('x-sort', true);
      g.attr('column-index', index);
      g.attr('column-name', label.name);
      g.style('visibility', 'hidden');
      g.attr('transform', `translate(${posX(index)}, ${firstYPos - 12})`);
      this.addSortControl(g, label.name, true);
      if (this._sorting.row === label.name)
        g.select(this._sorting.rowSortAscending ? '.sort-asc' : '.sort-desc').style('visibility', 'visible');
    });
    this._heatmap.axisYLabels.forEach((label, index) => {
      const g = sortLayerY.append('g').classed('y-sort', true);
      g.attr('row-index', index);
      g.attr('row-name', label.name);
      g.style('visibility', 'hidden');
      g.attr('transform', `translate(${firstXPos - 10}, ${posY(index)})`);
      this.addSortControl(g, label.name, false);
      if (this._sorting.col === label.name)
        g.select(this._sorting.colSortAscending ? '.sort-asc' : '.sort-desc').style('visibility', 'visible');
    });
  }

  private updateData(): void {
    const heatmap = this._heatmap;

    if (!heatmap) {
      return;
    }

    const r = this.updateCommon();

    const topicClickable = true;
    // this.model.heatmap.relatedData.filter(
    //   (x) => x.slice === 'topic' && x.items && x.items.length > 0
    // ).length > 0;
    const symbolClickable = true;
    // this.model.heatmap.relatedData.filter(
    //   (x) => x.slice === 'symbol' && x.items && x.items.length > 0
    // ).length > 0;
    const valueClickable = true;
    // this.model.heatmap.relatedData.filter(
    //   (x) => x.slice === 'value' && x.items && x.items.length > 0
    // ).length > 0;

    const renderTooltip = this.renderTooltip.bind(this);
    const tooltip = this._tooltip;

    r.names
      .enter()
      .append('text')
      .attr(
        'class',
        (d: any) =>
          `name name-index-${replaceLabel(d?.YLabel)}-${replaceLabel(d?.XLabel)}-${this._gridIndex}${
            symbolClickable ? ' name-pointer' : ''
          }`,
      )
      .on('click', (d, n: any) => {
        if (symbolClickable) {
          this._onClick({ source: 'symbol', symbol: n.id });
        }
      })
      .merge(r.names as any)
      .text(d => d.name)
      .attr('transform', d => {
        const offset = Math.abs(r.sin) > 0 ? 10 : 0;
        return `translate(${r.nameX(d.name) + offset}, ${r.nameY(d.name) - 4}) ${r.nameTransform(d.name)}`;
      })
      .style('fill', '#33485e')
      .style('font-size', '15px')
      .call(wrapText, this.namesOnTop, r.sin, r.cos)
      .on('mouseover', function (_d: any, n: any) {
        const labelRect = d3.select(this).attr('font-weight', 'bold').node()!.getBoundingClientRect();
        renderTooltip(labelRect, `<span>${n.name}</span>`, `<span>${n.description || ''}</span>`);
      })
      .on('mouseout', function (_d) {
        d3.select(this).attr('font-weight', 'normal');
        tooltip.style('opacity', 0);
      });

    r.names.exit().remove();

    r.rows
      .enter()
      .append('text')
      .attr('class', (d: any) => {
        return `row row-index-${replaceLabel(d?.YLabel)}-${replaceLabel(d?.XLabel)}-${this._gridIndex}${
          topicClickable ? ' row-pointer' : ''
        }`;
      })
      .attr('text-anchor', 'start')
      .on('click', (d, n: any) => {
        if (topicClickable) {
          this._onClick({ source: 'topic', topic: n.id });
        }
      })
      .merge(r.rows as any)
      .attr('x', d => {
        return r.rowX(d.name) - 10;
      })
      .attr('y', d => r.rowY(d.name))
      .style('fill', '#33485e')
      .style('font-size', '15px')
      .text(d => (d.name.length > 20 ? `${d.name.substring(0, 17)}...` : d.name))
      .on('mouseover', function (_d: any, n: any) {
        const labelRect = d3.select(this).attr('font-weight', 'bold').node()!.getBoundingClientRect();
        renderTooltip(
          labelRect,
          `<span>${n.name}</span>`,
          `<span>${n.description || ''}</span>
        `,
        );
      })
      .on('mouseout', function (_d) {
        d3.select(this).attr('font-weight', 'normal');
        tooltip.style('opacity', 0);
      });

    r.rows.exit().remove();

    const a: any = r.bars
      .enter()
      .append('rect')
      .attr('class', (d: any, n: any) => {
        return `bar index-${n}-${this._gridIndex}${valueClickable ? ' value-pointer' : ''}`;
      });

    this.setEventHandlersForBars(a)
      .merge(r.bars as any)
      .style('opacity', 0.8)
      .attr('width', () => r.x.bandwidth())
      .attr('height', () => r.y.bandwidth())
      .style('fill', d => r.myColor(d.Value))
      .attr('x', d => (this._prevAxisX ? this._prevAxisX(d.XLabel) : (r.x(d.XLabel) as any)))
      .attr('y', d => (this._prevAxisY ? this._prevAxisY(d.YLabel) : (r.y(d.YLabel) as any)))
      .transition()
      .duration(1000)
      .attr('x', d => r.x(d.XLabel) as any)
      .attr('y', d => r.y(d.YLabel) as any);

    r.bars.exit().remove();

    this._prevAxisY = r.y;
    this._prevAxisX = r.x;

    this.setEventHandlersForBars(r.bars);
    this.updateSortlayer(r);
  }

  private updateContainerSizes(clientWidth: number, clientHeight: number): void {
    const width: number = clientWidth - margin.left - margin.right;
    const height: number = clientHeight - margin.top - margin.bottom;

    d3.select(this.site).style('width', `${clientWidth}px`).style('height', `${clientHeight}px`);

    this._svg.attr('width', width + margin.left + margin.right).attr('height', height + margin.top + margin.bottom);

    if (this._axisX) {
      this._axisX.attr('transform', 'translate(0, 0)');
    }
  }
}
